import {useQuery} from '@tanstack/react-query';
import {fetchPublicProfile} from '../endpoints/api';

export interface PublicProfileConversationStarter {
  prompt_id: number;
  prompt: string;
  answer: string;
}

export interface PublicProfileAvailability {
  general: string[];
  specific: Record<string, string[]>;
}

export interface MutualConnection {
  id: number;
  name: string;
  profilePhotoUrl: string;
  handle: string;
}

export type ConnectionStatus = 'none' | 'pending' | 'connected';

export interface PublicProfile {
  id: number;
  name: string;
  surname: string;
  handle: string;
  profilePhotoUrl: string;
  hasActiveMembership: boolean;
  aboutMe: string;
  neighborhood: string;
  city: string;
  conversationStarters: PublicProfileConversationStarter[];
  availability: PublicProfileAvailability;
  cityDuration: string | null;
  neighborhoodDuration: string | null;
  mutualConnections: MutualConnection[];
  mutualConnectionCount: number;
  isCloseNeighbor: boolean;
  connectionStatus: ConnectionStatus;
  friendRequestId?: number;
  connectionsCount: number;
}

// Takes a numeric user ID (as string from URL param).
// TODO: backend currently only supports numeric {id}; if routes use handles,
// add a handle→id lookup endpoint.
export const usePublicProfile = (userId: string | undefined) => {
  return useQuery<PublicProfile>(
    ['publicProfile', userId],
    async () => {
      const res = await fetchPublicProfile(Number(userId));
      const d = (res as any)?.data?.data || (res as any)?.data;
      return {
        id: Number(userId),
        name: d.name,
        surname: d.surname || '',
        handle: d.handle || d.nickname || '',
        profilePhotoUrl: d.profilePhotoUrl || d.avatar || '',
        hasActiveMembership: d.hasActiveMembership ?? false,
        aboutMe: d.introduction || '',
        neighborhood: d.neighborhood || '',
        city: d.city || '',
        mutualConnections: (d.mutualConnections || []).map((mc: any) => ({
          id: mc.id,
          name: mc.name,
          profilePhotoUrl: mc.profilePhotoUrl || mc.profile_photo_url || '',
          handle: mc.handle || '',
        })),
        mutualConnectionCount: d.mutualConnectionCount ?? 0,
        isCloseNeighbor: d.isCloseNeighbor ?? false,
        connectionStatus: (d.connectionStatus || 'none') as ConnectionStatus,
        friendRequestId: d.friendRequestId ?? undefined,
        conversationStarters: (d.conversation_starters || []).map((s: any) => ({
          prompt_id: s.prompt_id,
          prompt: s.prompt,
          answer: s.answer,
        })),
        availability: {
          general: d.availability?.general || [],
          specific: d.availability?.specific || {},
        },
        cityDuration: d.city_profile?.city_duration ?? null,
        neighborhoodDuration: d.city_profile?.neighborhood_duration ?? null,
        connectionsCount: d.connections_count ?? 0,
      };
    },
    {
      enabled: !!userId && !isNaN(Number(userId)),
      staleTime: 30_000,
    }
  );
};
